import { memo, useEffect, useState, HTMLAttributes } from 'react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { i18n as I18n } from 'builder/utils/i18n'
import Icon from 'builder/components/Icon'
import Preview from 'builder/components/Preview'
import { ZoomTools } from 'builder/components/ZoomTools'
import { RenderPreviewProp } from '../../types'
import {
  MobilePaginationPanel,
  MobilePaginationCounter,
  MobilePaginationPrev,
  MobilePaginationNext,
  Content,
  PreviewBox,
  TemplateLabel,
} from './styles'
import { PreviewContentProps } from './types'

const PreviewContent = (props: PreviewContentProps & HTMLAttributes<HTMLDivElement>) => {
  const {
    contentRef,
    previewBoxRef,
    previewRef,
    type,
    width,
    height,
    isDataLoaded,
    currentDocument,
    currentTemplate,
    currentPage,
    totalPagesNumber,
    applyBodyScrollLock = true,
    handlePreviousPageClick,
    handleNextPageClick,
    handlePaginationStateChange,
    handlePreviewResize,
    renderPreview,
    ...rest
  } = props

  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false)

  const devicePixelRatio = window.devicePixelRatio || 1
  const pixelRatioNeededForZoomOnMobile = devicePixelRatio * 2
  const pixelRatio = isPreviewZoomed ? pixelRatioNeededForZoomOnMobile : devicePixelRatio

  useEffect(() => {
    const contentRefCurrent = contentRef.current
    window.addEventListener('resize', handlePreviewResize)
    handlePreviewResize()
    if (contentRefCurrent && applyBodyScrollLock) {
      disableBodyScroll(contentRefCurrent)
    }
    return () => {
      // Cleanup code or actions before the component is unmounted
      window.removeEventListener('resize', handlePreviewResize)
      if (contentRefCurrent && applyBodyScrollLock) {
        enableBodyScroll(contentRefCurrent)
      }
    }
  }, [contentRef, handlePreviewResize])

  const defaultRenderPreview: RenderPreviewProp = renderProps => {
    return <Preview {...renderProps} ref={renderProps.previewRef} />
  }

  const finalRenderPreview = renderPreview || defaultRenderPreview

  return (
    <Content ref={contentRef} $isPreviewZoomed={isPreviewZoomed} {...rest}>
      <PreviewBox ref={previewBoxRef}>
        {isDataLoaded && width > 0 && (
          <ZoomTools onExpandStateChange={value => setIsPreviewZoomed(value)}>
            {finalRenderPreview({
              height,
              width,
              document: currentDocument,
              documentId: currentDocument.id,
              type,
              previewRef,
              onPaginationChange: handlePaginationStateChange,
              pixelRatio,
            })}
          </ZoomTools>
        )}
        {totalPagesNumber > 1 && (
          <MobilePaginationPanel>
            <MobilePaginationPrev isEnabled={currentPage > 1} onClick={handlePreviousPageClick}>
              <Icon.Chevron />
            </MobilePaginationPrev>
            <MobilePaginationCounter>
              {currentPage} {I18n.t('builder.resume_editor.of')} {totalPagesNumber}
            </MobilePaginationCounter>
            <MobilePaginationNext
              isEnabled={currentPage < totalPagesNumber}
              onClick={handleNextPageClick}
            >
              <Icon.Chevron />
            </MobilePaginationNext>
          </MobilePaginationPanel>
        )}
        {isDataLoaded && !currentTemplate?.premium && <TemplateLabel>Free</TemplateLabel>}
      </PreviewBox>
    </Content>
  )
}

export default memo(PreviewContent)
